import { ref, inject } from 'vue'
import { Cookies } from 'quasar'
import { isProduction } from 'src/helpers/env.helper'
import type { GtmSupport } from '@gtm-support/vue-gtm'

const COOKIE_DOMAIN = process.env.COOKIE_DOMAIN

export type ConsentPreferences = {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  diagnostics: boolean
}

type ConsentCookieValue = {
  preferences: ConsentPreferences
  timestamp: string
}

const CONSENT_COOKIE_NAME = 'cookieConsent'
const CONSENT_COOKIE_MAX_AGE_DAYS = 365

const getCookieDomain = (): string => {
  // Use environment variable if set, otherwise derive from hostname
  if (COOKIE_DOMAIN) {
    return COOKIE_DOMAIN
  }
  const hostname = window.location.hostname
  const parts = hostname.split('.')
  // Return .domain.tld format (e.g., .rooute.app)
  return '.' + parts.slice(-2).join('.')
}

const useUserConsent = () => {
  const cookieDomain = getCookieDomain()
  const gtm = inject<GtmSupport | undefined>('gtm')

  const cookiePreferences = ref<ConsentPreferences>({
    necessary: true,
    analytics: true,
    marketing: true,
    diagnostics: true,
  })
  const consentGivenAt = ref<Date>()

  const updateToolsBasedOnConsent = () => {
    if (!cookiePreferences.value) {
      return
    }

    const preferences = cookiePreferences.value

    // Enable/disable GTM based on analytics consent
    if (gtm && (preferences.analytics || preferences.marketing)) {
      gtm.enable(true)

      // Push consent state to dataLayer for GTM to use
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'consent_update',
        consent: {
          analytics: preferences.analytics ? 'granted' : 'denied',
          marketing: preferences.marketing ? 'granted' : 'denied',
          diagnostics: preferences.diagnostics ? 'granted' : 'denied',
        },
      })
    } else if (gtm) {
      gtm.enable(false)
    }
  }

  const loadConsent = () => {
    // Try to load from cookie first (for cross-subdomain sharing)
    const cookieValue = Cookies.get(CONSENT_COOKIE_NAME)

    if (cookieValue) {
      try {
        const parsed: ConsentCookieValue = JSON.parse(cookieValue)
        cookiePreferences.value = parsed.preferences
        consentGivenAt.value = new Date(parsed.timestamp)
        updateToolsBasedOnConsent()
        return
      } catch {
        // Invalid cookie data, will use defaults
      }
    }

    // Fallback: check localStorage for legacy data and migrate to cookie
    const localStorageValue = localStorage.getItem(CONSENT_COOKIE_NAME)
    if (localStorageValue) {
      try {
        const parsed: ConsentCookieValue = JSON.parse(localStorageValue)
        cookiePreferences.value = parsed.preferences
        consentGivenAt.value = new Date(parsed.timestamp)

        // Migrate to cookie
        saveToCookie(parsed)

        // Remove old localStorage entry
        localStorage.removeItem(CONSENT_COOKIE_NAME)

        updateToolsBasedOnConsent()
      } catch {
        // Invalid localStorage data, will use defaults
      }
    }
  }

  loadConsent()

  const isConsentGiven = () => {
    return !!Cookies.get(CONSENT_COOKIE_NAME)
  }

  const saveToCookie = (value: ConsentCookieValue) => {
    Cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(value), {
      expires: CONSENT_COOKIE_MAX_AGE_DAYS,
      path: '/',
      domain: cookieDomain,
      sameSite: 'Lax',
      secure: isProduction(),
    })
  }

  // Save user preferences
  const saveConsent = () => {
    if (!cookiePreferences.value) {
      cookiePreferences.value = {
        necessary: true,
        analytics: false,
        marketing: false,
        diagnostics: false,
      }
    }

    const cookieValue: ConsentCookieValue = {
      preferences: cookiePreferences.value,
      timestamp: new Date().toISOString(),
    }

    saveToCookie(cookieValue)
    consentGivenAt.value = new Date()

    // Update tools based on new consent
    updateToolsBasedOnConsent()
  }

  // Handle user accepting all cookies
  const acceptAll = () => {
    cookiePreferences.value = {
      necessary: true,
      analytics: true,
      marketing: true,
      diagnostics: true,
    }
    saveConsent()
  }

  // Handle user selecting minimum cookies
  const acceptNecessary = () => {
    cookiePreferences.value = {
      necessary: true,
      analytics: false,
      marketing: false,
      diagnostics: false,
    }
    saveConsent()
  }

  return {
    cookiePreferences,
    acceptAll,
    acceptNecessary,
    saveConsent,
    isConsentGiven,
  }
}

export default useUserConsent

// Declare dataLayer on window
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
  }
}
